/**
 * Client-side mirror of the backend's yearly-plan date rules
 * (SubscriptionRenewalService.applyYearlyPurchase / createActiveSubscription), so the UI can
 * show the exact term a purchase will grant BEFORE the customer is sent to the payment page.
 * Keep in sync with the backend — these functions predict, the backend decides.
 */

/** Mirror of SubscriptionRenewalService.YEARLY_TERM_MONTHS — a rolling 12 months from purchase. */
export const YEARLY_TERM_MONTHS = 12;

/**
 * Local calendar date as YYYY-MM-DD. NOT toISOString(), which is UTC and reports yesterday's
 * date before 04:00 in Baku.
 */
export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Java LocalDate.plusMonths semantics: an overflowing day-of-month clamps to the target
 * month's last day (2026-08-31 + 1 month = 2026-09-30), unlike the JS Date constructor,
 * which rolls it into the next month.
 */
export function addMonthsClamped(iso, months) {
  const [y, m, d] = iso.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1 + months, 1));
  const daysInTarget = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const day = Math.min(d, daysInTarget);
  return `${first.getUTCFullYear()}-${String(first.getUTCMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * True while a yearly plan still has prepaid months left — the state in which the backend
 * rejects switching to any other plan (monthly or a different yearly tier).
 * `sub` is the CurrentSubscriptionDTO from /subscriptions/my.
 */
export function yearlyTermActive(sub) {
  return !!sub
    && sub.billingType === "YEARLY"
    && sub.status === "ACTIVE"
    && !!sub.paidThrough
    && sub.paidThrough > todayIso(); // ISO strings compare correctly
}

/**
 * Predict the term a yearly purchase will grant:
 *  - re-buying the SAME plan while the term runs EXTENDS it: new end = paidThrough + 12 months;
 *  - otherwise the yearly starts TODAY — an active monthly is cut at the moment of switch (its
 *    unused days are credited against the price, see predictUpgradeCredit), never run in
 *    parallel or stacked.
 * Returns { start, end, extension } as ISO dates.
 */
export function predictYearlyTerm(sub, planCode) {
  const today = todayIso();
  if (yearlyTermActive(sub) && sub.code === planCode) {
    return { start: today, end: addMonthsClamped(sub.paidThrough, YEARLY_TERM_MONTHS), extension: true };
  }
  return { start: today, end: addMonthsClamped(today, YEARLY_TERM_MONTHS), extension: false };
}

/** Whole days between two ISO dates (b - a). */
function daysBetween(a, b) {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

/**
 * Mirror of SubscriptionRenewalService.prorateUnusedMonthly: the day-prorated value of the
 * UNUSED part of an ACTIVE monthly period, credited against the yearly price when upgrading —
 * so the confirm modal can show the exact charge before the redirect. Keep in sync with the
 * backend (same day counts, same HALF_UP rounding to 2 decimals). Returns 0 when there is no
 * active monthly to credit.
 */
export function predictUpgradeCredit(sub, monthlyPrice) {
  if (!sub || sub.billingType !== "MONTHLY" || sub.status !== "ACTIVE") return 0;
  if (!sub.startDate || !sub.endDate || monthlyPrice == null) return 0;
  const totalDays = daysBetween(sub.startDate, sub.endDate);
  if (totalDays <= 0) return 0;
  const remainingDays = Math.min(totalDays, Math.max(0, daysBetween(todayIso(), sub.endDate)));
  return Math.round((Number(monthlyPrice) * remainingDays * 100) / totalDays) / 100;
}
